import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import _ from 'lodash';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

import { DateMomentPipe } from '../../../../pipes';
import { AppStateHolder, TimeLine } from '../../../../shared';
import { UserActivitiesService } from '../user-activities.service';

/** Interface for typology data row used in accessibility table */
interface TypologyDataRow {
  name: string;
  opened: number;
  working: number;
  closed: number;
  total: number;
}

@Component({
    selector: 'typology-chart',
    templateUrl: 'activity-typologyChart.component.html',
    styleUrls: ['activity-typologyChart.component.scss'],
    standalone: true,
    imports: [TooltipModule, ChartModule, ProgressSpinnerModule, DatePipe, TranslateModule, DateMomentPipe]
})

export class ActivityTypologyChartComponent implements OnInit, OnChanges, OnDestroy {
  private appHolder = inject(AppStateHolder);
  private timeLine = inject(TimeLine);
  private tralsator = inject(TranslateService);
  private service = inject(UserActivitiesService);

  /** Reference to the accessible data table for focus management */
  @ViewChild('typologyDataTable') typologyDataTable: ElementRef<HTMLTableElement>;

  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;
  @Input() params: string;
  @Input() changedParams: any;
  @Input() widgetPosition: any;

  @Output() dimensionSelected: EventEmitter<any> = new EventEmitter();
  @Output() popup: EventEmitter<{ widx: number, pop: boolean, params: any }> = new EventEmitter();

  status: string;
  activities: any[];
  uniqTypologies = [];
  dataOfTypology: any;

  /** Typology data for accessibility table */
  typologyDataForTable: TypologyDataRow[] = [];

  openOptions: boolean = false;
  optionSelected: any;
  barOptions: any;

  finalDate: string;
  initialDate: string;

  requestsSubscribe: Subscription;
  shortDate: any = [{ format: "L" }];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {

  }

  ngOnInit(): void {
    if (!this.modify) {
      this.timeLine.init();
      this.loadActivities(this.params);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (this.changedParams != undefined && this.idx == this.changedParams.indx) {
        this.loadActivities(this.changedParams.params);
      }
    }
  }

  loadActivities(params: string) {

    this.status = 'busy';

    // Default applicati sempre: periodo settimana, tutti gli stati.
    // Vengono sovrascritti solo se l'utente ha salvato parametri validi.
    let dateType = 1; // Week
    let state = "All";

    const prms = this.parseParams(params);
    if (prms) {
      if (prms.date == 'Day')
        dateType = 0;
      else if (prms.date == 'Week')
        dateType = 1;
      else if (prms.date == 'Month')
        dateType = 2;
      else if (prms.date == 'all')
        dateType = 1;

      if (prms.state)
        state = prms.state;
    }

    // Calcola sempre initialDate/finalDate: senza un periodo valido la GET
    // non partirebbe e il widget resterebbe bloccato sullo spinner.
    this.dataClick(dateType);

    this.requestsSubscribe = this.service.getUserActivitiesFil(this.initialDate, this.finalDate, state, null)
      .subscribe({
        next: res => {
          this.status = 'ready';
          this.activities = res ?? [];
          this.typologyStats();
        },
        error: () => {
          // Senza error handler lo status resterebbe 'busy' = spinner infinito.
          this.status = 'ready';
          this.activities = [];
          this.typologyStats();
        }
      });
  }

  /**
   * Deserializza in modo difensivo i parametri salvati del widget.
   * Gestisce il doppio wrapping ({ params: "..." }) e i valori non validi
   * (oggetti gia deserializzati, "null", JSON malformato) ritornando null,
   * cosi da ricadere sui default senza far fallire il caricamento.
   */
  private parseParams(params: any): any {
    if (!params) return null;

    try {
      let prms = typeof params === 'string' ? JSON.parse(params) : params;
      if (prms && prms.params) {
        prms = typeof prms.params === 'string' ? JSON.parse(prms.params) : prms.params;
      }
      return prms ?? null;
    } catch {
      return null;
    }
  }

  typologyStats() {

    let typeWorkingStates = this.activities
      .map((type) => { return { name: type.activityTypology.name, ws: type.workingState } });

    let result = _(typeWorkingStates)
      .groupBy(x => x.name)
      .map((value, key) => ({ name: key, ws: value }))
      .value();

    this.uniqTypologies = result.map(type => { return type.name });

    let openState: number[] = [];
    let closeState: number[] = [];
    let workingState: number[] = [];
    let step = 1;

    result.forEach((n) => {
      var countBy = _.countBy(n.ws, 'ws');
      closeState.push(countBy["2"] == undefined ? 0 : countBy["2"]);
      workingState.push(countBy["1"] == undefined ? 0 : countBy["1"]);
      openState.push(countBy["0"] == undefined ? 0 : countBy["0"]);
    });

    // Order by total descending and cap to 10
    let totals = this.uniqTypologies.map((name, index) => ({
      name,
      open: openState[index],
      working: workingState[index],
      close: closeState[index],
      total: openState[index] + workingState[index] + closeState[index]
    }));

    totals.sort((a, b) => b.total - a.total);
    // Show up to 15 typologies (previously 10)
    totals = totals.slice(0, 15);

    const sortedUniq = totals.map(t => t.name);
    const sortedOpen = totals.map(t => t.open);
    const sortedWorking = totals.map(t => t.working);
    const sortedClose = totals.map(t => t.close);

    this.uniqTypologies = sortedUniq;

    // Populate accessibility data table
    this.typologyDataForTable = totals.map(t => ({
      name: t.name,
      opened: t.open,
      working: t.working,
      closed: t.close,
      total: t.total
    }));

    const maxVal = Math.max(0, ...sortedOpen, ...sortedWorking, ...sortedClose);
    if (maxVal > 10) step = 10;

    this.dataOfTypology = {
      labels: sortedUniq,
      datasets: [
        { label: this.tralsator.instant('chart-view.label-opened'), data: sortedOpen, backgroundColor: '#df3a2c', borderRadius: 4, barThickness: 16 },
        { label: this.tralsator.instant('chart-view.label-working'), data: sortedWorking, backgroundColor: '#e0972b', borderRadius: 4, barThickness: 16 },
        { label: this.tralsator.instant('chart-view.label-closed'), data: sortedClose, backgroundColor: '#43a047', borderRadius: 4, barThickness: 16 }
      ]
    };

    this.barOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: 'rgba(20,24,31,.92)', padding: 10, cornerRadius: 10, boxPadding: 4 }
      },
      scales: {
        x: { stacked: true, ticks: { stepSize: step, color: '#8B929C', font: { size: 11 } }, grid: { display: false }, border: { display: false } },
        y: { stacked: true, ticks: { autoSkip: false, color: '#5A626D', font: { size: 12, weight: '600' } }, grid: { display: false }, border: { display: false } }
      },
      animation: { duration: 800, easing: 'easeOutQuart', delay: (context) => context.dataIndex * 120 + context.datasetIndex * 80 }
    };
  }

  get CanvasHeight(): number {

    if (this.uniqTypologies.length >= 11 && this.uniqTypologies.length <= 20)
      // Increase height per additional label to avoid squashed y-axis labels
      return 230 + ((this.uniqTypologies.length - 10) * 24)
    if (this.uniqTypologies.length >= 20 && this.uniqTypologies.length <= 30)
      return 250 + ((this.uniqTypologies.length) * 5)
    if (this.uniqTypologies.length >= 31 && this.uniqTypologies.length <= 40)
      return 270 + ((this.uniqTypologies.length) * 7)
    if (this.uniqTypologies.length >= 41 && this.uniqTypologies.length <= 50)
      return 300 + ((this.uniqTypologies.length) * 9)
    if (this.uniqTypologies.length >= 51)
      return 20 * (this.uniqTypologies.length);

    return 230;

  }

  hasActivities(): Boolean {

    if (this.activities === undefined)
      return false;

    return (this.activities.length > 0);
  }

  options() {
    this.openOptions = !this.openOptions;
  }

  selected() {
    this.dimensionSelected.next(this.optionSelected);
  }

  actpop() {

    if (this.changedParams == undefined) {
      this.sendParams();
    }
    else {
      let val = this.appHolder.typologyGraphParams.filter(r => { return r.indx == this.idx });

      if (val.length > 0) {
        this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(val[0].params) });
      }
      else {
        this.sendParams();
      }
    }
  }

  sendParams() {

    if (this.params) {
      this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(this.params) });
    }
    else {
      this.popup.next({ widx: this.idx, pop: true, params: this.params });
    }
  }

  dataClick(dateType: number) {

    let day;
    let week;
    let month;

    switch (dateType) {
      case 0:

        day = this.timeLine.day.date;

        this.finalDate = this.initialDate = (day.getFullYear() + "/" + (day.getMonth() + 1) + "/" + day.getDate());
        break;
      case 1:

        week = this.timeLine.week;
        let d1 = week.days[0].date;
        let d2 = week.days[week.days.length - 1].date;

        this.initialDate = d1.getFullYear() + "/" + (d1.getMonth() + 1) + "/" + d1.getDate();
        this.finalDate = d2.getFullYear() + "/" + (d2.getMonth() + 1) + "/" + d2.getDate();

        break;

      case 2:

        this.initialDate = this.timeLine.month.date.getFullYear() + "/" + (this.timeLine.month.date.getMonth() + 1) + "/" + this.timeLine.month.date.getDate();
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "/" + (this.timeLine.month.lastDate.getMonth() + 1) + "/" + this.timeLine.month.lastDate.getDate();

        break;

      case 3:

        this.initialDate = null;
        this.finalDate = null;

        break;
    }
  }

  ngOnDestroy(): void {
    if (this.requestsSubscribe)
      this.requestsSubscribe.unsubscribe()
  }

  /**
   * Focuses the accessible data table for screen reader users.
   * This method is called when users activate the skip link.
   */
  focusDataTable(): void {
    if (this.typologyDataTable?.nativeElement) {
      this.typologyDataTable.nativeElement.focus();
    }
  }
}
